import React, { useState } from 'react';
import {
  Box,
  Container,
  Header,
  Input,
  SpaceBetween,
  Tiles,
  Textarea,
  Button,
} from '@amzn/awsui-components-core';
import { CollectionAudience } from '@amzn/awscat-pattern-library-service-model';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import BrowserHistory from '../utils/BrowserHistory';
import constants from '../../common/constants';
import RouterPaths, { EDIT_COLLECTION_PATH_PREFIX } from '../../common/RouterPaths';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { validateCollectionCreation, MAX_TITLE_LENGTH, MAX_DESCRIPTION_LENGTH } from './utils';

import '../../resources/styles/scss/components/collection/collection.scss';

const CreateCollection = () => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const flashbarMessageBroker = FlashbarMessageBroker.create();

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [permission, setPermission] = useState<CollectionAudience>(CollectionAudience.PRIVATE);
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);

  // @ts-expect-error TS7031 - strict migration
  const handleTitleInputChange = ({ detail }) => {
    if (detail.value.length <= MAX_TITLE_LENGTH) {
      setTitle(detail.value);
    }
  };

  // @ts-expect-error TS7031 - strict migration
  const handleDescriptionChange = ({ detail }) => {
    if (detail.value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(detail.value);
    }
  };

  // @ts-expect-error TS7031 - strict migration
  const handlePermissionChange = ({ detail }) => {
    setPermission(detail.value);
  };

  const renderTitleInput = () => {
    return (
      <Container
        header={
          <Header variant="h3" description="Add a short, descriptive title for your collection.">
            Title *
          </Header>
        }
      >
        <div className="create-collection-title-container">
          <Input onChange={handleTitleInputChange} value={title} ariaRequired />
          <Box color="text-body-secondary">{`${MAX_TITLE_LENGTH - title.length} ${constants.EDIT_COLLECTION_CHAR_LIMIT_TEXT}`}</Box>
        </div>
      </Container>
    );
  };

  const renderDescriptionInput = () => {
    return (
      <Container
        header={
          <Header
            variant="h3"
            description="Provide a short summary of your collection to be displayed in search results. Avoid repeating the title."
          >
            Description *
          </Header>
        }
      >
        <div className="create-collection-description-container">
          <Textarea onChange={handleDescriptionChange} value={description} ariaRequired />
          <Box color="text-body-secondary">
            {`${MAX_DESCRIPTION_LENGTH - description.length} ${constants.EDIT_COLLECTION_CHAR_LIMIT_TEXT}`}
          </Box>
        </div>
      </Container>
    );
  };

  const renderPermission = () => {
    return (
      <Container
        header={
          <Header variant="h3" description="This can be modified in the future">
            Who can view your collection?
          </Header>
        }
      >
        <div className="create-collection-permission-container">
          <Tiles
            onChange={handlePermissionChange}
            value={permission}
            items={[
              {
                label: constants.MY_COLLECTION_AUDIENCE_LABEL_ONLY_OWNER,
                value: CollectionAudience.PRIVATE,
              },
              {
                label: constants.MY_COLLECTION_AUDIENCE_LABEL_INTERNAL,
                value: CollectionAudience.INTERNAL,
              },
            ]}
            ariaRequired
          />
        </div>
      </Container>
    );
  };

  const renderActionItems = () => {
    return (
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={handleCancelCreatingCollection}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateCollection} loading={loadingCreate}>
            {constants.CREATE_COLLECTION}
          </Button>
        </SpaceBetween>
      </Box>
    );
  };

  const handleCancelCreatingCollection = () => {
    BrowserHistory.push(RouterPaths.MY_COLLECTION);
  };

  const handleCreateCollection = async () => {
    const { hasError, errors } = validateCollectionCreation(title, description);
    if (hasError) {
      flashbarMessageBroker.pushErrorMessage(errors.join(' '));
    } else {
      setLoadingCreate(true);
      const { collectionId } = await mplServiceClient.createCollection(
        title,
        description,
        permission,
      );
      setLoadingCreate(false);
      BrowserHistory.push(`${EDIT_COLLECTION_PATH_PREFIX}/${collectionId}`);
    }
  };

  return (
    <div className="create-collection-container">
      <Box variant="h2">Create collection</Box>
      <SpaceBetween direction="vertical" size="m">
        {renderTitleInput()}
        {renderDescriptionInput()}
        {renderPermission()}
        {renderActionItems()}
      </SpaceBetween>
    </div>
  );
};

export default CreateCollection;
